/**
 * Filters – Event handler setup
 */

import {
  setupDeselectableRadios,
  getUnits,
  formatHeight,
  sliderToKm,
  sliderToDisplayLabel,
  wantsKidsLabel,
} from '../../utils';
import { store } from '../../state';
import type { FiltersState } from './types';
import type { IntentEntry, InterestedInGender, RelationshipIntent } from '../../options';
import { getHeightSliderMax, heightSliderToCm, activitySliderToHours, formatActivityLabel } from './slider-utils';

/**
 * Setup mutual exclusivity for checkbox groups.
 * When an exclusive option (pnta, none) is checked, uncheck ALL others.
 * When any other option is checked, uncheck all exclusive options.
 */
function setupExclusiveCheckboxes(): void {
  const exclusiveCheckboxes = document.querySelectorAll('input[type="checkbox"][data-exclusive="true"]');

  exclusiveCheckboxes.forEach(exclusiveCheckbox => {
    const exclusiveCb = exclusiveCheckbox as HTMLInputElement;
    const groupName = exclusiveCb.name;

    // When exclusive option is checked, uncheck ALL others
    exclusiveCb.addEventListener('change', () => {
      if (exclusiveCb.checked) {
        const allOtherCheckboxes = document.querySelectorAll(
          `input[name="${groupName}"]`
        );
        allOtherCheckboxes.forEach(cb => {
          if (cb !== exclusiveCb) {
            (cb as HTMLInputElement).checked = false;
            // Trigger change event to update filter state
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });

    // When any non-exclusive option is checked, uncheck ALL exclusive options
    const nonExclusiveCheckboxes = document.querySelectorAll(
      `input[name="${groupName}"]:not([data-exclusive="true"])`
    );
    nonExclusiveCheckboxes.forEach(cb => {
      cb.addEventListener('change', () => {
        if ((cb as HTMLInputElement).checked) {
          const allExclusiveInGroup = document.querySelectorAll(
            `input[name="${groupName}"][data-exclusive="true"]`
          );
          allExclusiveInGroup.forEach(exCb => {
            (exCb as HTMLInputElement).checked = false;
            // Trigger change event to update filter state
            exCb.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      });
    });
  });
}

function handleCheckboxChange(
  filters: FiltersState,
  saveFn: (id: string) => void,
  fieldId: string,
  value: string,
  checked: boolean
): void {
  // Map field IDs to filter state keys
  const fieldMap: Record<string, keyof FiltersState> = {
    interested_in: 'interested_in',
    relationship_intents: 'relationship_intents',
    smoking: 'smoking',
    drinking: 'drinking',
    cannabis: 'cannabis',
    drugs: 'drugs',
    exercise_levels: 'exercise_levels',
    wants_more_kids: 'wants_more_kids',
    open_to_long_distance: 'open_to_long_distance',
    open_to_relocation: 'open_to_relocation',
    zodiac_signs: 'zodiac_signs',
    education_levels: 'education_levels',
    employment_statuses: 'employment_statuses',
    languages: 'languages',
    pets_ok_with: 'pets_ok_with',
    religions: 'religions',
    ethnicities: 'ethnicities',
    political_views: 'political_views',
    income_ranges: 'income_ranges',
    body_types: 'body_types',
    sexual_orientations: 'sexual_orientations',
    sexual_roles: 'sexual_roles',
    sexual_dynamics: 'sexual_dynamics',
  };

  const filterKey = fieldMap[fieldId];
  if (!filterKey) return;

  // DOM checkbox values are strings; union types are string subtypes so this cast is safe
  const arr = filters[filterKey] as unknown[];
  if (checked) {
    if (!arr.includes(value)) {
      arr.push(value);
    }
  } else {
    (filters as any)[filterKey] = (arr as string[]).filter(v => v !== value);
  }

  saveFn(fieldId);
}

function handleRadioChange(
  filters: FiltersState,
  saveFn: (id: string) => void,
  fieldId: string,
  value: string
): void {
  if (fieldId === 'has_kids') {
    filters.has_kids = value || null;
  } else if (fieldId === 'preferred_structure') {
    filters.preferred_structure = value || null;
  }

  saveFn(fieldId);
}

export function setupFilterHandlers(
  filters: FiltersState,
  saveFn: (indicatorId: string) => void,
  isLikesMode = false
): void {
  // Section toggles
  document.querySelectorAll('.form-section-header').forEach(header => {
    header.addEventListener('click', (e) => {
      e.preventDefault();
      const sectionName = (header as HTMLElement).dataset.section;
      const content = document.getElementById(`section-${sectionName}`);
      if (content) {
        header.classList.toggle('collapsed');
        content.classList.toggle('collapsed');
      }
    });
  });

  // Two-way matching toggle
  const twoWayToggle = document.getElementById('two-way-toggle') as HTMLInputElement;
  twoWayToggle?.addEventListener('change', () => {
    filters.two_way_match = twoWayToggle.checked;
    saveFn('two-way');
  });

  // Intelligence substrate toggles
  const substrateHumanToggle = document.getElementById('substrate-human') as HTMLInputElement;
  const substrateAiToggle = document.getElementById('substrate-ai') as HTMLInputElement;

  substrateHumanToggle?.addEventListener('change', () => {
    // Ensure at least one is checked
    if (!substrateHumanToggle.checked && !substrateAiToggle.checked) {
      substrateAiToggle.checked = true;
      filters.show_ai_users = true;
    }
    filters.show_human_users = substrateHumanToggle.checked;
    saveFn('substrate');
  });

  substrateAiToggle?.addEventListener('change', () => {
    // Ensure at least one is checked
    if (!substrateAiToggle.checked && !substrateHumanToggle.checked) {
      substrateHumanToggle.checked = true;
      filters.show_human_users = true;
    }
    filters.show_ai_users = substrateAiToggle.checked;
    saveFn('substrate');
  });

  // Distance slider
  const distanceSlider = document.getElementById('distance-slider') as HTMLInputElement;
  const distanceDisplay = document.getElementById('distance-display')!;
  const units = getUnits();

  const updateDistanceDisplay = () => {
    const pos = parseInt(distanceSlider?.value || '0');
    filters.distance_max_km = sliderToKm(pos, units);
    distanceDisplay.textContent = sliderToDisplayLabel(pos, units);
    saveFn('distance');
  };

  distanceSlider?.addEventListener('input', updateDistanceDisplay);

  // Activity slider (Last Active)
  const activitySlider = document.getElementById('activity-slider') as HTMLInputElement;
  const activityDisplay = document.getElementById('activity-display')!;

  const updateActivityDisplay = () => {
    const sliderPos = parseInt(activitySlider?.value || '0');
    const hours = activitySliderToHours(sliderPos);
    filters.recently_active_hours = hours;
    activityDisplay.textContent = formatActivityLabel(hours);
    saveFn('activity');
  };

  activitySlider?.addEventListener('input', updateActivityDisplay);

  // Height sliders
  const heightMinSlider = document.getElementById('height-min-slider') as HTMLInputElement;
  const heightMaxSlider = document.getElementById('height-max-slider') as HTMLInputElement;
  const heightDisplay = document.getElementById('height-display')!;

  const updateHeightDisplay = () => {
    const noMaxPos = getHeightSliderMax(units);
    let minPos = parseInt(heightMinSlider?.value || '0');
    let maxPos = parseInt(heightMaxSlider?.value || String(noMaxPos));

    // Ensure min doesn't exceed max (accounting for "No Min" at pos 0)
    if (minPos > 0 && maxPos < noMaxPos && minPos > maxPos) {
      if (heightMinSlider === document.activeElement) {
        heightMaxSlider.value = minPos.toString();
        maxPos = minPos;
      } else {
        heightMinSlider.value = maxPos.toString();
        minPos = maxPos;
      }
    }

    const minCm = heightSliderToCm(minPos, units, false);
    const maxCm = heightSliderToCm(maxPos, units, true);

    filters.height_min_cm = minCm;
    filters.height_max_cm = maxCm;

    const minText = minCm === null ? 'No Min' : formatHeight(minCm, units);
    const maxText = maxCm === null ? 'No Max' : formatHeight(maxCm, units);
    heightDisplay.textContent = `${minText} - ${maxText}`;

    saveFn('height');
  };

  heightMinSlider?.addEventListener('input', updateHeightDisplay);
  heightMaxSlider?.addEventListener('input', updateHeightDisplay);

  // Kids filter radio (deselectable)
  const kidsPrimaryGroup = document.getElementById('kids-primary');
  const kidsSecondaryFilters = document.getElementById('kids-secondary-filters');

  const updateKidsFilters = (value: string) => {
    filters.has_kids = value || null;
    filters.kids_has_kids = value === 'must_have';
    filters.kids_no_kids = value === 'must_not_have';

    // Reset secondary kids filters
    filters.kids_youngest_ages = [];
    filters.kids_counts = [];
    filters.kids_at_home = null;
    document.querySelectorAll('#kids_youngest_ages input[type="checkbox"]').forEach(cb => {
      (cb as HTMLInputElement).checked = false;
    });
    document.querySelectorAll('#kids_counts input[type="checkbox"]').forEach(cb => {
      (cb as HTMLInputElement).checked = false;
    });
    document.querySelectorAll('#kids_at_home input[type="radio"]').forEach(r => {
      (r as HTMLInputElement).checked = false;
    });

    // Show/hide secondary filters
    if (kidsSecondaryFilters) {
      kidsSecondaryFilters.style.display = filters.kids_has_kids ? '' : 'none';
    }

    const wantsMoreIndicator = document.getElementById('indicator-wants_more_kids');
    const wantsMoreLabel = wantsMoreIndicator?.parentElement?.querySelector('.group-label');
    if (wantsMoreLabel) {
      wantsMoreLabel.textContent = wantsKidsLabel(filters.kids_has_kids, 'filter');
    }

    saveFn('kids-filter');
  };

  if (kidsPrimaryGroup) {
    setupDeselectableRadios(kidsPrimaryGroup, (_name, value) => {
      updateKidsFilters(value);
    });
  }

  // Kids youngest ages checkboxes
  document.querySelectorAll('#kids_youngest_ages input[type="checkbox"]').forEach(checkbox => {
    checkbox.addEventListener('change', () => {
      const input = checkbox as HTMLInputElement;
      if (input.checked) {
        if (!filters.kids_youngest_ages.includes(input.value)) {
          filters.kids_youngest_ages.push(input.value);
        }
      } else {
        filters.kids_youngest_ages = filters.kids_youngest_ages.filter(v => v !== input.value);
      }
      saveFn('kids-age');
    });
  });

  // Kids counts checkboxes
  document.querySelectorAll('#kids_counts input[type="checkbox"]').forEach(checkbox => {
    checkbox.addEventListener('change', () => {
      const input = checkbox as HTMLInputElement;
      const count = parseInt(input.value);
      if (input.checked) {
        if (!filters.kids_counts.includes(count)) {
          filters.kids_counts.push(count);
        }
      } else {
        filters.kids_counts = filters.kids_counts.filter(v => v !== count);
      }
      saveFn('kids-count');
    });
  });

  // Kids at home radios
  document.querySelectorAll('#kids_at_home input[type="radio"]').forEach(radio => {
    radio.addEventListener('change', () => {
      const input = radio as HTMLInputElement;
      filters.kids_at_home = input.value || null;
      saveFn('kids-at-home');
    });
  });

  // Checkbox groups
  document.querySelectorAll('.checkbox-group input[type="checkbox"]').forEach(checkbox => {
    checkbox.addEventListener('change', () => {
      const input = checkbox as HTMLInputElement;
      const fieldId = input.dataset.filterField;
      if (!fieldId) return;

      handleCheckboxChange(filters, saveFn, fieldId, input.value, input.checked);
    });
  });

  // Radio groups (deselectable)
  document.querySelectorAll('.radio-group').forEach(group => {
    const fieldId = group.id;
    setupDeselectableRadios(group, (_name, value) => {
      handleRadioChange(filters, saveFn, fieldId, value);
    });
  });

  // Disclosure checkboxes (boolean filters)
  const inclusiveTransCheckbox = document.getElementById('inclusive_of_trans_nb') as HTMLInputElement;
  inclusiveTransCheckbox?.addEventListener('change', () => {
    filters.inclusive_of_trans_nb = inclusiveTransCheckbox.checked;
    saveFn('inclusive_of_trans_nb');
  });

  const openEnmPolyCheckbox = document.getElementById('open_to_enm_poly') as HTMLInputElement;
  openEnmPolyCheckbox?.addEventListener('change', () => {
    filters.open_to_enm_poly = openEnmPolyCheckbox.checked;
    saveFn('open_to_enm_poly');
  });

  // Activity filter checkboxes
  const activityLikedMe = document.getElementById('activity-liked-me') as HTMLInputElement;
  const activityILiked = document.getElementById('activity-i-liked') as HTMLInputElement;
  const activityIDisliked = document.getElementById('activity-i-disliked') as HTMLInputElement;

  // Skip attaching handler when in likes mode - checkbox is force-checked and disabled
  if (!isLikesMode) {
    activityLikedMe?.addEventListener('change', () => {
      filters.activity_liked_me = activityLikedMe.checked;
      saveFn('activity-filters');
    });
  }

  activityILiked?.addEventListener('change', () => {
    filters.activity_i_liked = activityILiked.checked;
    // Mutually exclusive with "I passed"
    if (activityILiked.checked && activityIDisliked) {
      activityIDisliked.checked = false;
      filters.activity_i_disliked = false;
    }
    saveFn('activity-filters');
  });

  activityIDisliked?.addEventListener('change', () => {
    filters.activity_i_disliked = activityIDisliked.checked;
    // Mutually exclusive with "I sent interest"
    if (activityIDisliked.checked && activityILiked) {
      activityILiked.checked = false;
      filters.activity_i_liked = false;
    }
    saveFn('activity-filters');
  });

  // Travel location inputs
  const travelCity = document.getElementById('travel-city') as HTMLInputElement;
  const travelState = document.getElementById('travel-state') as HTMLInputElement;
  const travelCountry = document.getElementById('travel-country') as HTMLInputElement;
  const travelResetBtn = document.getElementById('travel-reset-btn');

  const handleTravelInput = () => {
    const city = travelCity?.value.trim() || null;
    const state = travelState?.value.trim() || null;
    const country = travelCountry?.value.trim() || null;

    // Determine if this differs from profile location
    const profile = store.getState().user?.profile;
    const profileCity = profile?.city || '';
    const profileState = profile?.state || '';
    const profileCountry = profile?.country || '';
    const isDefault = (city || '') === profileCity && (state || '') === profileState && (country || '') === profileCountry;

    if (isDefault && !filters.travel_city && !filters.travel_state && !filters.travel_country) {
      // No change from profile defaults, don't set travel mode
      return;
    }

    filters.travel_city = city;
    filters.travel_state = state;
    filters.travel_country = country;
    // lat/lng will be set by server response
    filters.travel_lat = null;
    filters.travel_lng = null;

    // Show reset button
    if (travelResetBtn) travelResetBtn.style.display = '';

    saveFn('travel-location');
  };

  travelCity?.addEventListener('input', handleTravelInput);
  travelState?.addEventListener('input', handleTravelInput);
  travelCountry?.addEventListener('input', handleTravelInput);

  // Reset button
  travelResetBtn?.addEventListener('click', () => {
    filters.travel_city = null;
    filters.travel_state = null;
    filters.travel_country = null;
    filters.travel_lat = null;
    filters.travel_lng = null;

    // Re-fill inputs from profile location
    const profile = store.getState().user?.profile;
    if (travelCity) travelCity.value = profile?.city || '';
    if (travelState) travelState.value = profile?.state || '';
    if (travelCountry) travelCountry.value = profile?.country || '';

    // Hide reset button
    travelResetBtn.style.display = 'none';

    // Update feedback display
    const display = document.getElementById('travel-location-display');
    if (display) {
      display.classList.remove('text-error');
      if (profile?.latitude != null && profile?.longitude != null) {
        display.textContent = `${Number(profile.latitude).toFixed(4)}, ${Number(profile.longitude).toFixed(4)}`;
      } else {
        display.textContent = 'Set your location in your profile';
      }
    }

    saveFn('travel-location');
  });

  // Setup exclusive checkbox behavior (PNTA unchecks others)
  setupExclusiveCheckboxes();

  // Setup intent entry handlers
  setupIntentEntryHandlers(filters, saveFn);
}

/**
 * Setup handlers for compound intent entry CRUD (add, remove, edit genders/intents/age).
 * Re-renders the entries list on structural changes (add/remove).
 */
function setupIntentEntryHandlers(
  filters: FiltersState,
  saveFn: (indicatorId: string) => void
): void {
  const listEl = document.getElementById('intent-entries-list');
  const addBtn = document.getElementById('add-intent-entry-btn');
  if (!listEl || !addBtn) return;

  const reRender = () => {
    // Dynamically import renderIntentEntryCard to avoid circular deps
    // Instead, rebuild the list HTML inline
    const { renderIntentEntriesListHtml } = getIntentEntryRenderers();
    listEl.innerHTML = renderIntentEntriesListHtml(filters.intent_entries);
    bindEntryFieldListeners();
    saveFn('intent-entries');
  };

  // Add button
  addBtn.addEventListener('click', () => {
    filters.intent_entries.push({ genders: [], intents: [], age_min: 18, age_max: 99 });
    reRender();
  });

  const bindEntryFieldListeners = () => {
    // Remove buttons
    listEl.querySelectorAll('.btn-remove-intent-entry').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt((e.currentTarget as HTMLElement).dataset.entryIndex || '0');
        filters.intent_entries.splice(index, 1);
        // Sync flat arrays from remaining entries
        syncFlatFieldsFromEntries(filters);
        reRender();
      });
    });

    // Gender checkboxes within entries
    listEl.querySelectorAll('input[data-entry-field="genders"]').forEach(cb => {
      cb.addEventListener('change', () => {
        const input = cb as HTMLInputElement;
        const idx = parseInt(input.dataset.entryIndex || '0');
        const entry = filters.intent_entries[idx];
        if (!entry) return;
        if (input.checked) {
          if (!entry.genders.includes(input.value as InterestedInGender)) {
            entry.genders.push(input.value as InterestedInGender);
          }
        } else {
          entry.genders = entry.genders.filter(g => g !== input.value);
        }
        syncFlatFieldsFromEntries(filters);
        saveFn('intent-entries');
      });
    });

    // Intent checkboxes within entries
    listEl.querySelectorAll('input[data-entry-field="intents"]').forEach(cb => {
      cb.addEventListener('change', () => {
        const input = cb as HTMLInputElement;
        const idx = parseInt(input.dataset.entryIndex || '0');
        const entry = filters.intent_entries[idx];
        if (!entry) return;
        if (input.checked) {
          if (!entry.intents.includes(input.value as RelationshipIntent)) {
            entry.intents.push(input.value as RelationshipIntent);
          }
        } else {
          entry.intents = entry.intents.filter(i => i !== input.value);
        }
        syncFlatFieldsFromEntries(filters);
        saveFn('intent-entries');
      });
    });

    // Age range sliders within entries
    listEl.querySelectorAll('.intent-entry-age-min, .intent-entry-age-max').forEach(slider => {
      slider.addEventListener('input', () => {
        const input = slider as HTMLInputElement;
        const idx = parseInt(input.dataset.entryIndex || '0');
        const entry = filters.intent_entries[idx];
        if (!entry) return;

        const isMin = input.classList.contains('intent-entry-age-min');
        const val = parseInt(input.value);

        if (isMin) {
          entry.age_min = val;
          // Ensure min <= max
          if (val > (entry.age_max ?? 99)) {
            entry.age_max = val;
            const maxSlider = listEl.querySelector(
              `.intent-entry-age-max[data-entry-index="${idx}"]`
            ) as HTMLInputElement;
            if (maxSlider) maxSlider.value = val.toString();
          }
        } else {
          entry.age_max = val;
          // Ensure max >= min
          if (val < (entry.age_min ?? 18)) {
            entry.age_min = val;
            const minSlider = listEl.querySelector(
              `.intent-entry-age-min[data-entry-index="${idx}"]`
            ) as HTMLInputElement;
            if (minSlider) minSlider.value = val.toString();
          }
        }

        // Update display
        const display = listEl.querySelector(
          `.intent-entry-age-display[data-entry-index="${idx}"]`
        );
        if (display) {
          const min = entry.age_min ?? 18;
          const max = entry.age_max ?? 99;
          display.textContent = (min === 18 && max === 99) ? 'Any' : `${min}-${max}`;
        }
      });

      slider.addEventListener('change', () => {
        syncFlatFieldsFromEntries(filters);
        saveFn('intent-entries');
      });
    });
  };

  // Initial binding
  bindEntryFieldListeners();
}

/**
 * Derive flat interested_in and relationship_intents from intent_entries
 * for backward compatibility. Union of all genders and all intents across entries.
 */
function syncFlatFieldsFromEntries(filters: FiltersState): void {
  if (filters.intent_entries.length === 0) {
    filters.interested_in = [];
    filters.relationship_intents = [];
    return;
  }

  const genders = new Set<InterestedInGender>();
  const intents = new Set<RelationshipIntent>();
  for (const entry of filters.intent_entries) {
    entry.genders.forEach(g => genders.add(g));
    entry.intents.forEach(i => intents.add(i));
  }
  filters.interested_in = Array.from(genders);
  filters.relationship_intents = Array.from(intents);
}

/**
 * Returns renderers for intent entry list HTML.
 * Kept as a function to avoid importing sections.ts (would be circular).
 */
function getIntentEntryRenderers() {
  // Inline import of options for labels
  const GENDER_OPTS = [
    { value: 'men', label: 'Man' },
    { value: 'women', label: 'Woman' },
    { value: 'nonbinary', label: 'Non-binary' },
  ];

  // Intent options imported at module level already
  const INTENT_OPTS = [
    { value: 'friends', label: 'Friends' },
    { value: 'activity_partner', label: 'Activity Partner' },
    { value: 'dating', label: 'Dating' },
    { value: 'long_term', label: 'Long-term' },
    { value: 'marriage', label: 'Marriage' },
    { value: 'casual', label: 'Casual' },
    { value: 'exploring', label: 'Exploring' },
    { value: 'hookup', label: 'Hookup' },
  ];

  function renderIntentEntriesListHtml(entries: IntentEntry[]): string {
    if (!entries || entries.length === 0) {
      return '<div class="empty-intent-entries">No entries yet. Add one to specify what you\'re looking for.</div>';
    }
    return entries.map((entry, i) => `
      <div class="intent-entry-card" data-entry-index="${i}">
        <div class="intent-entry-header">
          <span class="intent-entry-number">Entry ${i + 1}</span>
          <button type="button" class="btn-icon btn-remove-intent-entry" data-entry-index="${i}" title="Remove entry">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="intent-entry-fields">
          <div class="intent-entry-field">
            <span class="group-label">Their Gender</span>
            <div class="checkbox-group">
              ${GENDER_OPTS.map(opt => `
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    name="intent_entry_genders_${i}"
                    value="${opt.value}"
                    ${entry.genders?.includes(opt.value as any) ? 'checked' : ''}
                    data-entry-index="${i}"
                    data-entry-field="genders"
                  />
                  ${opt.label}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="intent-entry-field">
            <span class="group-label">Relationship Goals</span>
            <div class="checkbox-group">
              ${INTENT_OPTS.map(opt => `
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    name="intent_entry_intents_${i}"
                    value="${opt.value}"
                    ${entry.intents?.includes(opt.value as any) ? 'checked' : ''}
                    data-entry-index="${i}"
                    data-entry-field="intents"
                  />
                  ${opt.label}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="intent-entry-field">
            <div class="preference-row">
              <span class="group-label">Age Range</span>
              <span class="filter-value intent-entry-age-display" data-entry-index="${i}">${(entry.age_min ?? 18) === 18 && (entry.age_max ?? 99) === 99 ? 'Any' : `${entry.age_min ?? 18}-${entry.age_max ?? 99}`}</span>
            </div>
            <div class="flex gap-2">
              <input type="range" class="input intent-entry-age-min" data-entry-index="${i}" min="18" max="99" value="${entry.age_min ?? 18}" />
              <input type="range" class="input intent-entry-age-max" data-entry-index="${i}" min="18" max="99" value="${entry.age_max ?? 99}" />
            </div>
          </div>
        </div>
      </div>
    `).join('');
  }

  return { renderIntentEntriesListHtml };
}
